import { useState, useEffect, useCallback } from 'react';
import { useScript } from './general';
import { REACT_APP_FACEBOOK_APP_ID } from 'config/env';

const STATUS_CONNECTED = 'connected';
const STATUS_NOT_AUTHORISED = 'not_authorized';
const STATUS_UNKNOWN = 'unknown';
const DEFAULT_USER_STATE = {
  name: '',
  email: '',
  isChecked: false,
  isConnected: false,
  status: STATUS_UNKNOWN
};

const checkIsUserLoggedIn = user => {
  return user.status === STATUS_CONNECTED;
};

const checkIsUserUnauthroised = user => {
  return user.status === STATUS_NOT_AUTHORISED;
};

// WARNING: This hook has a limitation on setting the fb init status in state as it is not shared
// across the app. To check the fb status on initilization, please use `checkIsFbInit` and if you are
// checking that in an effect, sadly this will require you to make the effect run everytime to check
// as we don't have a global init state.
// Eg: useEffect(() => { checkIsFbInit() && doSomethingRelatedToFb() }) // No second param so this effect will run every time we render the component
export const useFbScript = () => {
  const appId = REACT_APP_FACEBOOK_APP_ID;
  const appVersion = 'v5.0';
  const ERROR_FB_NOT_INIT = 'FB is not initialize yet';

  const [loaded, error] = useScript('//connect.facebook.net/en_US/sdk.js');
  const [events, setEvents] = useState([]);

  useEffect(() => {
    if (loaded && window.FB) {
      try {
        window.FB.init({
          appId: appId,
          version: appVersion,
          cookie: true,
          xfbml: true
        });

        window.FB.AppEvents.logPageView();
      } catch (ex) {
        console.error('Facebook SDK failed to initialize. Error message as below:');
        console.error(ex);
      }
    }
  }, [loaded, appId]);

  useEffect(() => {
    if (error) {
      console.error('Facebook SDK failed to initialize. Error message as below:');
    }
  }, [error]);

  const checkIsFbInit = useCallback(() => {
    return !!window.FB;
  }, []);

  const reparseFbXml = () => {
    return window.FB && window.FB.XFBML.parse();
  };

  const getFbUserBasicDetails = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (checkIsFbInit()) {
        window.FB.api('/me', { fields: 'id,email,name' }, response => {
          if (!response) {
            reject(new Error('No response return'));
          } else if (response.error) {
            reject(response.error);
          } else {
            resolve(response);
          }
        });
      } else {
        reject({ message: ERROR_FB_NOT_INIT });
      }
    });
  }, [checkIsFbInit]);

  const getFbLoginStatus = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (checkIsFbInit()) {
        window.FB.getLoginStatus(response => {
          resolve(response);
        });
      } else {
        reject({ message: ERROR_FB_NOT_INIT });
      }
    });
  }, [checkIsFbInit]);

  const loginFb = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (checkIsFbInit()) {
        window.FB.login(
          response => {
            resolve(response);
          },
          {
            scope: 'public_profile,email',
            return_scopes: true
          }
        );
      } else {
        reject({ message: ERROR_FB_NOT_INIT });
      }
    });
  }, [checkIsFbInit]);

  const logoutFb = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (checkIsFbInit()) {
        window.FB.logout(response => {
          resolve(response);
        });
      } else {
        reject({ message: ERROR_FB_NOT_INIT });
      }
    });
  }, [checkIsFbInit]);

  const subscribeFbEvent = useCallback(
    (event, callback) => {
      if (checkIsFbInit()) {
        setEvents([...events, event]);
        return window.FB.Event.subscribe(event, callback);
      }
    },
    [events, checkIsFbInit]
  );

  const unsubscribeFbEvent = useCallback(
    (event, callback) => {
      if (checkIsFbInit() && events.includes(event)) {
        const index = events.indexOf(event);
        if (index >= 0) events.splice(index, 1);
        setEvents([...events]);
        return window.FB.Event.unsubscribe(event, callback);
      }
    },
    [checkIsFbInit, events]
  );

  const shareToFb = useCallback(
    url => {
      if (checkIsFbInit()) {
        window.FB.ui({
          method: 'share',
          href: url
        });
      }
    },
    [checkIsFbInit]
  );

  return { checkIsFbInit, reparseFbXml, getFbLoginStatus, getFbUserBasicDetails, subscribeFbEvent, unsubscribeFbEvent, loginFb, logoutFb, shareToFb };
};

export const useFbUser = () => {
  const { checkIsFbInit, getFbLoginStatus, getFbUserBasicDetails, loginFb, logoutFb } = useFbScript();
  const [user, setUser] = useState(DEFAULT_USER_STATE);
  const updateUser = useCallback(
    newUser => {
      const combinedNewUser = { ...user, ...newUser };
      setUser({ ...combinedNewUser, isChecked: true, isConnected: checkIsUserLoggedIn(combinedNewUser) });
    },
    [user]
  );

  const onLoginResponse = useCallback(
    async response => {
      if (checkIsUserLoggedIn(response)) {
        try {
          const userDetails = await getFbUserBasicDetails();
          updateUser({ ...userDetails, status: STATUS_CONNECTED });
        } catch (ex) {
          updateUser({ isChecked: true });
          console.log('Something went wrong when fetching fb user details');
          console.error(ex);
        }
      } else if (checkIsUserUnauthroised(response)) {
        updateUser({ status: STATUS_NOT_AUTHORISED });
      } else {
        updateUser({ status: STATUS_UNKNOWN });
      }
    },
    [getFbUserBasicDetails, updateUser]
  );

  useEffect(() => {
    if (!user.isChecked && checkIsFbInit()) {
      const setFbLoginStatus = async () => {
        updateUser({ isChecked: true });
        const response = await getFbLoginStatus();
        onLoginResponse(response);
      };
      setFbLoginStatus();
    }
  });

  const loginToFb = useCallback(() => {
    return loginFb().then(response => {
      onLoginResponse(response);
      return response;
    });
  }, [loginFb, onLoginResponse]);

  const logoutFromFb = useCallback(() => {
    return logoutFb().then(() => updateUser(DEFAULT_USER_STATE));
  }, [logoutFb, updateUser]);

  return { user, loginFb: loginToFb, logoutFb: logoutFromFb };
};
